// "As seen on" · real media coverage of the FSC Mauritius licence announcement.
// Business Insider & Yahoo Finance render as CSS-mask SVG logos (theme-tinted);
// the rest are brand-faithful wordmarks so the whole strip stays monochrome.
// Desktop: static row · Mobile: auto-scrolling carousel (logos don't fit).
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";

export const PRESS_OUTLETS = [
  {
    id: "business-insider", name: "Business Insider",
    href: "https://markets.businessinsider.com/news/currencies/ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence-1036331921",
    mask: "/press/business-insider.svg", w: 172, h: 16,
  },
  {
    id: "yahoo-finance", name: "Yahoo Finance",
    href: "https://finance.yahoo.com/markets/currencies/articles/ofinancial-strengthens-global-regulatory-presence-102000226.html",
    mask: "/press/yahoo-finance.svg", w: 148, h: 21,
  },
  {
    id: "apnews", name: "AP News",
    href: "https://apnews.com/press-release/globenewswire-mobile/press-release-7078601156dea7a435d45a0fdaeac6e2",
    mask: "/press/apnews.png", w: 25, h: 22,
  },
  {
    id: "tradingview", name: "TradingView",
    href: "https://www.tradingview.com/news/financewire:c57c7b19f094b:0-ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence/",
    mask: "/press/tradingview.png", w: 137, h: 18,
  },
  {
    id: "investing", name: "Investing.com",
    href: "https://www.investing.com/news/company-news/ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence-4795191",
    mask: "/press/investing.png", w: 118, h: 20,
  },
  {
    id: "benzinga", name: "Benzinga",
    href: "https://www.benzinga.com/content/60490705/ofinancial-strengthens-global-regulatory-presence-new-mauritius-fsc-licence",
    mask: "/press/benzinga.png", w: 121, h: 17,
  },
  {
    id: "fxstreet", name: "FXStreet",
    href: "https://www.fxstreet.com/press-releases/ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence-202607161037",
    mask: "/press/fxstreet.svg", w: 83, h: 20,
  },
  {
    id: "myfxbook", name: "Myfxbook",
    href: "https://www.myfxbook.com/press-release/ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence/51782",
    mask: "/press/myfxbook.png", w: 84, h: 22,
  },
  {
    id: "advfn", name: "ADVFN",
    href: "https://www.advfn.com/stock-market/stock-news/98930495/ofinancial-strengthens-global-regulatory-presence",
    mask: "/press/advfn.svg", w: 67, h: 22,
  },
  {
    id: "financefeeds", name: "FinanceFeeds",
    href: "https://financefeeds.com/ofinancial-strengthens-global-regulatory-presence-with-new-mauritius-fsc-licence/",
    mask: "/press/financefeeds.png", w: 172, h: 18,
  },
];

function OutletLink({ o, scale = 1, className = "", suffix = null, testId = null }) {
  const w = Math.round(o.w * scale);
  const h = Math.round(o.h * scale);
  const ext = o.mask.endsWith(".svg") ? "svg" : "png";
  const base = o.mask.replace(/\.(svg|png)$/, "");
  return (
    <a
      href={o.href}
      target="_blank"
      rel="noopener noreferrer"
      title={o.name}
      aria-label={`${o.name} — press coverage`}
      data-testid={testId || `press-logo-${o.id}`}
      className={`press-logo ${className}`}
    >
      <span className="relative inline-block" style={{ width: w, height: h }}>
        <span
          aria-hidden
          className="press-mask absolute inset-0"
          style={{ WebkitMaskImage: `url(${o.mask})`, maskImage: `url(${o.mask})`, width: w, height: h }}
        />
        <img aria-hidden alt="" src={`${base}-c-light.${ext}`} width={w} height={h} className="press-color press-color-light absolute inset-0" style={{ width: w, height: h }} />
        <img aria-hidden alt="" src={`${base}-c-dark.${ext}`} width={w} height={h} className="press-color press-color-dark absolute inset-0" style={{ width: w, height: h }} />
      </span>
      {suffix}
    </a>
  );
}
export { OutletLink as PressLogo };

export default function FeaturedIn() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="as-seen-on"
      className="py-10 lg:py-12"
      style={{
        borderTop: "1px solid color-mix(in srgb, var(--text-3) 12%, transparent)",
        borderBottom: "1px solid color-mix(in srgb, var(--text-3) 12%, transparent)",
        background: "color-mix(in srgb, var(--gold-2) 2%, transparent)",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center text-xs font-bold uppercase tracking-[0.24em]" style={{ color: "var(--gold-2)" }}>
          {t("comp.misc.asSeenOn", "As seen on")}
        </div>

        {/* Desktop · single static row */}
        <div className="hidden md:flex mt-7 flex-wrap items-center justify-center gap-x-10 gap-y-6 lg:gap-x-12">
          {PRESS_OUTLETS.map((o) => <OutletLink key={o.id} o={o} />)}
        </div>

        {/* Mobile · auto-scrolling carousel */}
        <div
          className="md:hidden mt-6"
          data-testid="as-seen-on-carousel"
          style={{
            WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 40px, #000 calc(100% - 40px), transparent 100%)",
            maskImage: "linear-gradient(90deg, transparent 0, #000 40px, #000 calc(100% - 40px), transparent 100%)",
          }}
        >
          <Marquee gradient={false} speed={34} pauseOnHover>
            {PRESS_OUTLETS.map((o) => <OutletLink key={o.id} o={o} className="mx-8" />)}
          </Marquee>
        </div>
      </div>
    </section>
  );
}
